
import axios from 'axios/index'
import { getAxiosConfig } from '@/utils/apiUtils'
import { formatDateForOnlineRequest } from '@/utils/date'
// import ConfLoader from '@/api/confLoader'

export const publicBase = '/public/'
export const privateBase = '/'

class CommonApi {
  getApiEntry(apiRoute = '') {
    if (!process.env.VUE_APP_BACKEND_URL) {
      throw new Error('FATAL: you must define VUE_APP_BACKEND_URL and recompile front. (see .env file)')
    }

    return process.env.VUE_APP_BACKEND_URL.concat(apiRoute)
  }

  ping() {
    return axios.get(this.getApiEntry('/security/ping'), getAxiosConfig('ping'))
  }

  // Company functions
  searchCompany(id) {
    return axios.get(this.getApiEntry('/company/' + id), getAxiosConfig('search_company'))
  }

  companyInputFields(uniqueChain = null) {
    let url

    if (uniqueChain) {
      url = '/public/company/input_fields/' + uniqueChain
    } else {
      url = '/company/InputFields'
    }

    let key = 'company_custom_fields'

    if (uniqueChain !== null) key += `_${uniqueChain}`

    return axios.get(this.getApiEntry(url), getAxiosConfig(key))
  }

  getGenericCompanyData(uniqueChain) {
    return axios.get(this.getApiEntry('/public/company/' + uniqueChain), getAxiosConfig('company_genric_info'))
  }

  getUnloggedCompanyData(uniqueChain, values = null) {
    let url = this.getApiEntry('/public/company/' + uniqueChain)

    if (values) url = url.concat('/', values)

    return axios.get(url, getAxiosConfig('company_unlogged_data'))
  }

  getCompanyGroupData(companyGroupId) {
    return axios.get(this.getApiEntry('/public/company_group/' + companyGroupId), getAxiosConfig('company_group_data'))
  }

  companyHasOnlyOne(uniqueChain) {
    return axios.get(this.getApiEntry('/company/only_one/' + uniqueChain),
      getAxiosConfig('search_company_only_one'))
  }

  companyRandomUniqueChain() {
    return axios.get(this.getApiEntry('/company/random_unique_chain'), getAxiosConfig('company_random_unique_chain'))
  }

  companySearchCriterias() {
    return axios.get(this.getApiEntry('/company_search_criterias'),
      getAxiosConfig('search_company_criterias'))
  }

  companyFavoriteLocations(uniqueChain = null) {
    let url

    if (uniqueChain) {
      url = '/public/company/locations/' + uniqueChain
    } else {
      url = '/company/locations'
    }

    return axios.get(this.getApiEntry(url), getAxiosConfig('company_favorite_locations'))
  }

  companyFavoriteHotels(locationId, uniqueChain = null) {
    let url

    if (uniqueChain) {
      url = '/public/company/location/' + locationId + '/hotels/' + uniqueChain
    } else {
      url = '/company/location/' + locationId + '/hotels'
    }

    return axios.get(this.getApiEntry(url), getAxiosConfig('company_favorite_hotels_by_location'))
  }

  listUsers(perPage, currentPage, orderBy, order, search = null) {
    return axios.post(this.getApiEntry(privateBase + 'company/users'), {
      perPage: perPage,
      currentPage: currentPage,
      orderBy: orderBy,
      order: order,
      search: search
    }, getAxiosConfig('users_list'))
  }

  swapUserEnabled(userId, state) {
    return axios.get(this.getApiEntry(privateBase + 'user_swap_enabled/' + userId), getAxiosConfig('user_swap_enabled'))
  }

  swapUserSendVoucherToTravellers() {
    return axios.get(this.getApiEntry(privateBase + 'user_swap_send_voucher_to_travellers'), getAxiosConfig('user_swap_send_voucher_to_travellers'))
  }

  editUser(data) {
    let url = privateBase.concat(
      'user/',
      data['id'] !== null ? 'edit' : 'create'
    )

    return axios.post(this.getApiEntry(url), data, getAxiosConfig('user_edit'))
  }

  // Travellers functions
  swapTravellerEnabled (travellerId) {
    return axios.get(this.getApiEntry(privateBase + 'traveller/' + travellerId + '/swap_enabled'), getAxiosConfig('traveller_swap_enabled'))
  }

  listTravellers(max = 10, isPublic = false, companyUniqueChain = '') {
    let base = isPublic ? publicBase : privateBase

    return axios.get(this.getApiEntry(base + 'travellers?max=' + max + '&unique=' + companyUniqueChain), getAxiosConfig('list_travellers'))
  }

  listTravellersAll(search = null, enabled = 'all', perPage, currentPage, orderBy, order) {
    let url = '/travellers_all'
    url += '?search=' + search
    url += '&enabled=' + enabled
    url += '&per_page=' + perPage
    url += '&current_page=' + currentPage
    url += '&order_by=' + orderBy
    url += '&order=' + order
    return axios.get(this.getApiEntry(url), getAxiosConfig('list_travellers_all'))
  }

  searchTraveller(search, max = 100, uniqueChain = null) {
    let url

    if (uniqueChain) {
      url = publicBase + 'travellers?search=' + search + '&max=' + max + '&unique=' + uniqueChain
    } else {
      url = '/travellers?search=' + search + '&max=' + max
    }

    return axios.get(this.getApiEntry(url), getAxiosConfig('search_travellers'))
  }

  getTravellerCustomFields(travellerId, isPublic = false) {
    let base = isPublic ? publicBase : privateBase

    return axios.get(this.getApiEntry(base + 'traveller/' + travellerId + '/custom_fields'), getAxiosConfig('traveller_custom_fields_' + travellerId))
  }

  // Booking functions
  bookingMaxTravellers() {
    return 6 // 6 travellers max by reservation
  }

  searchLocation(locationName, isPublic = false) {
    let base = isPublic ? publicBase : privateBase

    return axios.post(this.getApiEntry(base + 'hrs/locations/search'), { location_name: locationName }, getAxiosConfig())
  }

  bookingCancellationReasons() {
    return axios.get(this.getApiEntry('/booking/cancellation_reasons'), getAxiosConfig('cancellation_reasons'))
  }

  async bookingCustomFields(bookingId) {
    return axios.get(this.getApiEntry('/booking/customFields/' + bookingId), getAxiosConfig('booking_custom_fields'))
  }

  reservationsList(filters = null) {
    let params = {}

    if (filters !== null) params.filters = filters

    return axios.post(
      this.getApiEntry('/bookings'),
      params,
      getAxiosConfig('list_bookings')
    )
  }

  reservationListFilterOptions() {
    return axios.get(this.getApiEntry('/bookings/filters'), getAxiosConfig('booking_filters'))
  }

  bookingsStats(group) {
    return axios.get(this.getApiEntry('/bookings/stats?group=' + group), getAxiosConfig('bookings_stats_' + group))
  }

  historyReservationsList(
    perPage = 10,
    currentPage,
    orderBy,
    order,
    filters = null
  ) {
    return axios.post(
      this.getApiEntry('/bookings/history'),
      {
        per_page: perPage,
        current_page: currentPage,
        order_by: orderBy,
        order: order,
        filters: filters
      },
      getAxiosConfig('history_reservation_list')
    )
  }

  searchBooking(id, validation = false, getActions = false) {
    if (validation) {
      return axios.get(this.getApiEntry('/booking/' + id + '/validation'), getAxiosConfig('get_booking_by_validation_id'))
    } else {
      return axios.get(this.getApiEntry('/booking/' + id + '?get_actions=' + getActions), getAxiosConfig('get_booking'))
    }
  }

  searchBookingPublic (token) {
    return axios.get(this.getApiEntry('/public/booking/' + token + '/show'), getAxiosConfig('get_booking'))
  }

  bookingCancellationReasonsPublic (token) {
    return axios.get(this.getApiEntry('/public/booking/' + token + '/cancellation_reasons'), getAxiosConfig('cancellation_reasons'))
  }

  searchBookingValidation(validationId, id, getActions = false) {
    let url = this.getApiEntry('/public/booking/' + validationId + '/validation')
    if (id) url += '?id=' + id

    return axios.get(url, getAxiosConfig('get_booking_by_validation_id'))
  }

  getHrsBookingDetail(id) {
    return axios.get(this.getApiEntry('/hrs/booking/detail/' + id), getAxiosConfig())
  }

  searchHotelDetail(id) {
    return axios.get(this.getApiEntry('/hrs/hotels/detail/' + id), getAxiosConfig())
  }

  getSelectedOffers(data) {
    return axios.post(this.getApiEntry('/hrs/hotels/offers'), this.parseSearchOnlineRequest(data), getAxiosConfig())
  }

  bindUserToBookings(userId, bookings) {
    return axios.post(this.getApiEntry('/public/bind_user_to_bookings'), {
      userId: userId, bookings: bookings
    }, getAxiosConfig('bind_user_to_bookings'))
  }

  getUnfinishedOnlineBooking(id) {
    return axios.get(this.getApiEntry('/booking/online/unfinished/' + id), getAxiosConfig('get_unfinished_online_booking'))
  }

  disableBooking(id) {
    return axios.get(this.getApiEntry('/booking/disable/' + id), getAxiosConfig('disable_booking'))
  }

  adminDisableBooking(id) {
    return axios.get(this.getApiEntry('/booking/admin_disable/' + id), getAxiosConfig('admin_disable_booking'))
  }

  adminDisableHotelRetrocession(id) {
    return axios.get(this.getApiEntry('/hotelretrocessions/admin_disable/' + id), getAxiosConfig('admin_disable_hotel_retrocession'))
  }

  adminEnableHotelRetrocession(id) {
    return axios.get(this.getApiEntry('/hotelretrocessions/admin_enable/' + id), getAxiosConfig('admin_enable_hotel_retrocession'))
  }

  // Message functions
  listMessages(messageId = 0) {
    return axios.get(this.getApiEntry('/messages/list?message_id=' + messageId), getAxiosConfig('messages_list'))
  }

  sendParentMessage(formData) {
    return axios.post(
      this.getApiEntry('/message/send/parent'),
      formData,
      getAxiosConfig('message_send_parent', 'application/form-data')
    )
  }

  sendChildMessage(formData) {
    return axios.post(
      this.getApiEntry('/message/send/child'),
      formData,
      getAxiosConfig('message_send_child', 'application/form-data')
    )
  }

  setSeenMessages(parentId) {
    return axios.get(
      this.getApiEntry('/message/seen/' + parentId),
      getAxiosConfig('message_seen')
    )
  }

  getNbMessagesAndNbNotif() {
    return axios.get(
      this.getApiEntry('/user/nb_messages_and_nb_notifs'),
      getAxiosConfig('nb_messages_and_nb_notifs')
    )
  }

  // Notification functions
  getNotifications() {
    return axios.get(this.getApiEntry('/user/notifications'), getAxiosConfig('user_notifications'))
  }

  // News functions
  listNews() {
    return axios.get(this.getApiEntry('/news/current'), getAxiosConfig('list_news'))
  }

  getNbUnreadNews() {
    // hack - disable temporarly function
    return 0
    // return axios.get(this.getApiEntry('/user/nb_unread_news'), getAxiosConfig('get_nb_unread_news'))
  }

  updateReadNews() {
    return axios.get(this.getApiEntry('/news/update_read'), getAxiosConfig('update_read_news'))
  }

  // Login functions
  notifyLostPassword(email) {
    return axios.post(this.getApiEntry('/public/notify_lost_password'), { email: email }, getAxiosConfig('notify_lost_password'))
  }

  isDefinedPasswordRouteDisplayable(token) {
    return axios.get(this.getApiEntry('/public/is_route_available/' + token), getAxiosConfig('is_route_available'))
  }

  isDefinedContingentPasswordRouteDisplayable(token) {
    return axios.get(this.getApiEntry('/public/is_contingent_route_available/' + token), getAxiosConfig('is_route_available'))
  }

  resetUserTempPasswordToken(values) {
    return axios.post(this.getApiEntry('/public/reset_user_temp_password_token'), values, getAxiosConfig('reset_user_temp_password_token'))
  }

  // Travel Policy functions
  getZone(id) {
    return axios.get(this.getApiEntry(privateBase + 'zone/' + id), getAxiosConfig('get_zone'))
  }

  searchCityOrPostCode(search) {
    return axios.get(
      this.getApiEntry(privateBase + 'zone/search_by_city_or_post_code/' + search),
      getAxiosConfig('search_city_or_post_code')
    )
  }

  getTravelPolicy(companyId) {
    return axios.get(
      this.getApiEntry(privateBase + 'travel_policy/' + companyId),
      getAxiosConfig('get_travel_policy')
    )
  }

  getTravelPolicySelectData(type) {
    if (!['zone', 'department', 'region'].includes(type)) return

    return axios.get(
      this.getApiEntry(privateBase + 'travel_policy_select_' + `${type}s`),
      getAxiosConfig(`get${type}`)
    )
  }

  deleteTravelPolicyRow(id) {
    return axios.get(
      this.getApiEntry(privateBase + 'travel_policy/' + id + '/delete'),
      getAxiosConfig(`delete_travel_policy_row_${id}`)
    )
  }

  // Fields White Mark functions

  getFieldsWhiteMark (companyId) {
    return axios.get(
      this.getApiEntry(privateBase + 'fields_white_mark/' + companyId),
      getAxiosConfig('get_fields_white_mark')
    )
  }

  deleteFieldsWhiteMark (companyId) {
    return axios.get(
      this.getApiEntry(privateBase + 'fields_white_mark/' + companyId + '/delete'),
      getAxiosConfig(`delete_fields_white_mark_${companyId}`)
    )
  }

  // Other functions
  listEvents(filters) {
    if (filters.startDate) { filters.startDate = formatDateForOnlineRequest(filters.startDate) }
    if (filters.endDate) { filters.endDate = formatDateForOnlineRequest(filters.endDate) }

    return axios.post(this.getApiEntry('/events/current'), filters, getAxiosConfig('list_events'))
  }

  listHelp() {
    return axios.get(this.getApiEntry('/help/current'), getAxiosConfig('list_help'))
  }

  surveyQuestions() {
    return axios.get(this.getApiEntry('/survey/questions'), getAxiosConfig('search_survey_questions'))
  }

  surveyQuestionsPublic (token) {
    return axios.get(this.getApiEntry('/public/survey/' + token + '/questions'),
      getAxiosConfig('public_search_survey_questions'))
  }

  downloadSpreadsheetBookingsUrl(filters) {
    let from = filters.dates.from
    let to = filters.dates.to
    let type = filters.type ? filters.type.value : ''
    let bookingRef = filters.bookingRef ? filters.bookingRef.value : ''

    let url = '/download/bookings'
    url += `?booking-ref=${bookingRef}`
    url += `&type=${type}`
    url += `&hotel=${filters.hotel.map(data => data.value).join('_')}`
    url += `&status=${filters.status.map(data => data.value).join('_')}`
    url += `&from=${from instanceof Date ? formatDateForOnlineRequest(from) : ''}`
    url += `&to=${to instanceof Date ? formatDateForOnlineRequest(to) : ''}`

    for (let i in filters.ref) {
      url += `&ref[]=${filters.ref[i].value}`
    }

    return this.getApiEntry(url)
  }

  downloadBookingUrl(id, format) {
    return this.getApiEntry('/download/booking/' + id + '/' + format)
  }

  downloadOrderPdfUrl(id) {
    return this.getApiEntry('/download/order_pdf/booking/' + id)
  }

  downloadMessageAttachment(id) {
    return this.getApiEntry('/download/message/' + id + '/attachment')
  }

  downloadTravellerImportExample(companyId) {
    if (companyId) { return this.getApiEntry().concat('/download/traveller_import/example/' + companyId) }
    return this.getApiEntry().concat('/download/traveller_import/example/')
  }

  parseSearchOnlineRequest(values) {
    // Copy variable (not by reference)
    let newValues = JSON.parse(JSON.stringify(values))

    newValues.startDate = formatDateForOnlineRequest(new Date(values.startDate))
    newValues.endDate = formatDateForOnlineRequest(new Date(values.endDate))

    return newValues
  }

  parseOfflineRequestValues(values, uniqueChain = null) {
    // Copy variable (not by reference)
    let newValues = JSON.parse(JSON.stringify(values))

    newValues.dates.from = formatDateForOnlineRequest(values.dates.from)
    newValues.dates.to = formatDateForOnlineRequest(values.dates.to)

    if (uniqueChain) newValues.uniqueChain = uniqueChain

    return newValues
  }

  getResendEmailUrl(mailLogId) {
    return this.getApiEntry('/maillog/' + mailLogId + '/send')
  }

  getOfflineFormationFormInfos () {
    return axios.get(this.getApiEntry('/formation/offline/infos'), getAxiosConfig('formation_offline_form_infos'))
  }

  submitOfflineFormationForm (data) {
    return axios.post(this.getApiEntry('/formation/offline/submit'), data, getAxiosConfig('user_edit'))
  }

  getCBConfigurations () {
    return axios.get(this.getApiEntry('/configurations/cb'))
  }

  getCBPublicKey () {
    return axios.get(this.getApiEntry('/configurations/cb/public_key'), getAxiosConfig('get_cb_public_key'))
  }

  listHotels(
    perPage = 10,
    currentPage,
    orderBy,
    order,
    filters = null
  ) {
    return axios.get(
      this.getApiEntry('/hotels'),
      {
        per_page: perPage,
        current_page: currentPage,
        order_by: orderBy,
        order: order,
        filters: filters
      },
      getAxiosConfig('hotels')
    )
  }

  getHotelId () {
    return axios.get(this.getApiEntry('/contingents_hotel/hotel/own'), getAxiosConfig('hotel_own'))
  }

  getHotelGammes (hotelId) {
    return axios.get(this.getApiEntry('/hotelgammes?hotel__id[eq]=' + hotelId), getAxiosConfig('hotel_gammes'))
  }
}

export default CommonApi
